import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useBlogPost from '../hooks/useBlogPost';

const BlogPost = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { post, content, loading, notFound } = useBlogPost(id);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex flex-col">
                <Navbar />
                <main className="flex-1 container max-w-3xl mx-auto px-6 lg:px-8 py-16">
                    <div className="animate-pulse space-y-6">
                        <div className="h-3 bg-gray-800 rounded w-24" />
                        <div className="h-8 bg-gray-800 rounded w-3/4" />
                        <div className="h-4 bg-gray-800 rounded w-48" />
                        <div className="space-y-3 pt-8">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className={`h-4 bg-gray-800 rounded ${i % 3 === 2 ? 'w-2/3' : 'w-full'}`} />
                            ))}
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="min-h-screen bg-gray-900 text-gray-300 flex flex-col">
                <Navbar />
                <div className="flex-1 flex flex-col items-center justify-center gap-4">
                    <p className="text-gray-400 text-lg">Post not found.</p>
                    <Link to="/" className="text-orange-500 hover:underline text-sm">
                        &larr; Back to blog
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-gray-300 flex flex-col">
            <Navbar />

            <main className="flex-1 container max-w-3xl mx-auto px-6 lg:px-8 py-16">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-orange-500 transition-colors mb-12"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                </button>

                <header className="mb-12 pb-10 border-b border-gray-800">
                    <span className="text-orange-500 text-xs tracking-widest uppercase font-medium">
                        {post.category}
                    </span>
                    <h1 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-5 leading-tight">
                        {post.title}
                    </h1>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{post.date}</span>
                        <span>&middot;</span>
                        <span>{post.readTime}</span>
                    </div>
                </header>

                <div className="prose prose-invert prose-orange max-w-none
                    prose-headings:font-bold prose-headings:text-white
                    prose-p:text-gray-400 prose-p:leading-relaxed
                    prose-li:text-gray-400
                    prose-code:text-orange-400 prose-code:bg-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:before:content-none prose-code:after:content-none
                    prose-pre:bg-gray-800 prose-pre:border prose-pre:border-gray-700
                    prose-a:text-orange-400 prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-gray-200
                    prose-hr:border-gray-800">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {content}
                    </ReactMarkdown>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default BlogPost;
